import { useCallback, useEffect, useMemo, useState } from 'react';
import { Calendar, Download, Play } from 'lucide-react';
import { UniversalWindow } from '@/components/ui/UniversalWindow';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ReportResultTable, type ReportResult } from '@/components/reports/ReportResultTable';
import { exportReport, renderReport, ReportFormat, useReport } from '@/api/hooks/useReports';
import { friendlyError, logErrorToJournal } from '@/lib/errors';

// ============================================================================
// ReportViewDialog — просмотр отчёта В ТЕКУЩЕЙ ОБОЛОЧКЕ (Предприятие), Ф1:
// панель ПАРАМЕТРОВ отчёта (из определения — v1/v2; даты — с пресетами),
// таблица с сортировкой/фильтром/сворачиванием/форматами (ReportResultTable),
// экспорт CSV/Excel/PDF, ошибки явно + журнал. Рендер — СЕРВЕРНЫЙ (RenderReport);
// конфигураторный предпросмотр (ReportPreviewPage) — отдельный (диагностика).
// ============================================================================

interface ReportViewDialogProps {
    reportId: string;
    title?: string;
    onClose: () => void;
}

interface ReportParam { name: string; label?: string; type?: string; defaultValue?: unknown }
interface DefinitionMeta {
    params: ReportParam[];
    fieldTypes: Record<string, { type: string; format: string }>;
    hasSources: boolean;
}

// parseDefinitionMeta — параметры/реквизиты/наличие источников из определения
// (ОБА формата: v1 dataSources/fields/parameters и v2 query.*).
function parseDefinitionMeta(definitionJson: string): DefinitionMeta {
    const meta: DefinitionMeta = { params: [], fieldTypes: {}, hasSources: false };
    try {
        const d = JSON.parse(definitionJson || '{}') as Record<string, any>;
        const isV2 = d.version === 2 || (d.query && d.layout);
        const q = isV2 ? (d.query || {}) : d;
        meta.params = (q.parameters || []) as ReportParam[];
        for (const f of (q.fields || []) as Array<Record<string, any>>) {
            const name = String(f.name || '');
            if (name) meta.fieldTypes[name] = { type: String(f.type || ''), format: String(f.format || '') };
        }
        meta.hasSources = (q.dataSources || []).length > 0;
    } catch (e) {
        logErrorToJournal(e, 'ReportViewDialog.parseDefinitionMeta');
    }
    return meta;
}

function todayISO(): string {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function shiftISO(days: number): string {
    const d = new Date();
    d.setDate(d.getDate() + days);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function ReportViewDialog({ reportId, title, onClose }: ReportViewDialogProps) {
    const { report } = useReport(reportId);
    const [result, setResult] = useState<ReportResult | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [running, setRunning] = useState(false);
    const [values, setValues] = useState<Record<string, string>>({});
    const [applied, setApplied] = useState<Record<string, string>>({});

    const meta = useMemo(
        () => parseDefinitionMeta((report as any)?.definitionJson || (report as any)?.definition_json || ''),
        [report],
    );

    const run = useCallback(async (params: Record<string, string>) => {
        setRunning(true);
        setError(null);
        try {
            const clean: Record<string, string> = {};
            for (const [k, v] of Object.entries(params)) {
                if (String(v).trim() !== '') clean[k] = v;
            }
            const res = await renderReport({ id: reportId, parametersJson: JSON.stringify(clean), format: ReportFormat.JSON });
            setResult(res as unknown as ReportResult);
            setApplied(clean);
        } catch (e: any) {
            setError(friendlyError(e));
            logErrorToJournal(e, 'ReportViewDialog.run');
        } finally {
            setRunning(false);
        }
    }, [reportId]);

    useEffect(() => {
        run({});
    }, [run]);

    const handleExport = async (fmt: ReportFormat) => {
        try {
            await exportReport({ id: reportId, parametersJson: JSON.stringify(applied), format: fmt });
        } catch (e: any) {
            setError(friendlyError(e));
            logErrorToJournal(e, 'ReportViewDialog.export');
        }
    };

    const paramsApplied = Object.keys(applied).length > 0;

    return (
        <UniversalWindow
            open
            title={`Отчёт: ${(report as any)?.name || title || ''}`}
            width="w-[min(1000px,96vw)]"
            height="h-[min(80vh,92vh)]"
            onClose={onClose}
            topBar={(
                <div className="flex items-center gap-2">
                    <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => handleExport(ReportFormat.CSV)}>
                        <Download className="h-3 w-3 mr-1" /> CSV
                    </Button>
                    <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => handleExport(ReportFormat.XLSX)}>
                        <Download className="h-3 w-3 mr-1" /> Excel
                    </Button>
                    <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => handleExport(ReportFormat.PDF)}>
                        <Download className="h-3 w-3 mr-1" /> PDF
                    </Button>
                    <Button size="sm" className="h-7 text-[11px]" onClick={() => run(values)} disabled={running}>
                        <Play className="h-3 w-3 mr-1" /> {running ? 'Выполняется…' : 'Обновить'}
                    </Button>
                </div>
            )}
        >
            <div className="space-y-3">
                {/* Панель параметров отчёта (из определения; даты — пресеты) */}
                {meta.params.length > 0 && (
                    <div className="border rounded-md px-3 py-2 space-y-2 bg-muted/20">
                        <div className="text-[11px] font-medium text-muted-foreground">Параметры отчёта</div>
                        <div className="flex flex-wrap items-end gap-3">
                            {meta.params.map((p) => (
                                <div key={p.name} className="space-y-1">
                                    <div className="text-[10px] text-muted-foreground">{p.label || p.name}</div>
                                    {String(p.type || '') === 'date' ? (
                                        <div className="flex items-center gap-1">
                                            <Input type="date" value={values[p.name] ?? ''} onChange={(e) => setValues((v) => ({ ...v, [p.name]: e.target.value }))} className="h-7 text-xs w-[150px]" />
                                            <Button size="sm" variant="outline" className="h-7 px-1.5 text-[10px]" title="Сегодня" onClick={() => setValues((v) => ({ ...v, [p.name]: todayISO() }))}>
                                                <Calendar className="h-3 w-3" />
                                            </Button>
                                            <Button size="sm" variant="outline" className="h-7 px-1.5 text-[10px]" onClick={() => setValues((v) => ({ ...v, [p.name]: shiftISO(-7) }))}>7д</Button>
                                            <Button size="sm" variant="outline" className="h-7 px-1.5 text-[10px]" onClick={() => setValues((v) => ({ ...v, [p.name]: shiftISO(-30) }))}>30д</Button>
                                        </div>
                                    ) : String(p.type || '') === 'boolean' ? (
                                        <input type="checkbox" className="rounded"
                                            checked={values[p.name] === 'true'}
                                            onChange={(e) => setValues((v) => ({ ...v, [p.name]: e.target.checked ? 'true' : '' }))} />
                                    ) : (
                                        <Input type={String(p.type || '') === 'number' ? 'number' : 'text'}
                                            value={values[p.name] ?? ''}
                                            onChange={(e) => setValues((v) => ({ ...v, [p.name]: e.target.value }))}
                                            className="h-7 text-xs w-[180px]" />
                                    )}
                                </div>
                            ))}
                            <Button size="sm" className="h-7 text-[11px]" onClick={() => run(values)} disabled={running}>Применить</Button>
                        </div>
                    </div>
                )}

                {error && <div className="text-sm text-destructive bg-destructive/10 rounded-md px-4 py-2">{error}</div>}
                {!result && !error && <div className="text-sm text-muted-foreground">Загрузка…</div>}
                {result && !error && (
                    <ReportResultTable
                        result={result}
                        fieldTypes={meta.fieldTypes}
                        hasSources={meta.hasSources}
                        paramsApplied={paramsApplied}
                    />
                )}
            </div>
        </UniversalWindow>
    );
}
